import { Component, EventEmitter, Inject, OnInit, Output, ChangeDetectionStrategy } from '@angular/core';
import { FormArray, FormBuilder, FormGroup } from '@angular/forms';
import { MAT_DIALOG_DATA, MatDialog, MatDialogConfig, MatDialogRef } from '@angular/material/dialog';
import { ErrorService, LayoutService } from '@core/services';
import { EmployerProfileStoreService } from '@core/services/employer-profile-store.service';
import { ManageEmployersService } from '@core/services/manage-employers.service';
import { indicate, noChangesReplacer, closeDialogOnEscape } from '@shared/helpers';
import { position, onDragEnded } from '@shared/helpers/dialog-drag-constraints';
import { Subject, Observable } from 'rxjs';
import { UnsavedChangesComponent } from '../unsaved-changes/unsaved-changes.component';
import { takeUntil } from 'rxjs/operators';
import { CustomNameFormGroup, EditEmployerLeaveTypeCatNamesForm, LeaveTypeCatNamePost } from '@core/models/leave-admin/employers/employer-leave-type-cat-names.model';
import { CustomName, EmployerProfile } from '@core/models/leave-admin/employers/employer-profile.model';
import { StiiraError } from '@core/models';

@Component({
    selector: 'app-edit-employer-leave-type-cat-names',
    templateUrl: './edit-employer-leave-type-cat-names.component.html',
    styleUrl: './edit-employer-leave-type-cat-names.component.scss',
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class EditEmployerLeaveTypeCatNamesComponent implements OnInit {
  static readonly componentName = 'EditEmployerLeaveTypeCatNamesComponent';
  
  @Output() isEditing = new EventEmitter<boolean>();

  public form: FormGroup<EditEmployerLeaveTypeCatNamesForm>;
  public isSubmitting$: Subject<boolean> = new Subject<boolean>();

  public position = position;
  public onDragEnded = onDragEnded;

  private formInitValues: any;
  private destroy$: Subject<void> = new Subject<void>();
  private formChangeEmitted: boolean = false;

  get noChanges(): boolean {
    return JSON.stringify(this.form.value, noChangesReplacer) === JSON.stringify(this.formInitValues, noChangesReplacer);
  }

  public isMobile$: Observable<boolean>;

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: { 
      employerProfile: EmployerProfile;
      sysText: any;
    },
    private fb: FormBuilder,
    private layoutService: LayoutService,
    private dialogRef: MatDialogRef<EditEmployerLeaveTypeCatNamesComponent>,
    private store: EmployerProfileStoreService,
    private dialog: MatDialog,
    private service: ManageEmployersService,
    private errorService: ErrorService,
  ) { 
    this.form = this.fb.group<EditEmployerLeaveTypeCatNamesForm>({
      leaveCategories: new FormArray<FormGroup<CustomNameFormGroup>>([])
    });
  }

  ngOnInit(): void {
    this.isMobile$ = this.layoutService.isMobile$();

    closeDialogOnEscape(this.dialogRef, this.isSubmitting$, () => this.close(false));

    this.setupForm();
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  public onSubmit() {
    const dto: LeaveTypeCatNamePost = {
      employerId: this.data.employerProfile.employerId,
      leaveCategories: []
    }

    this.form.controls.leaveCategories.controls.forEach(formGroup => {
      dto.leaveCategories.push({
        id: formGroup.controls.id.value,
        customName: formGroup.controls.customName.value?.trim() ?? null
      });
    });

    this.service.postLeaveTypeCatNamesEdit(dto)
      .pipe(indicate(this.isSubmitting$))
      .subscribe((res)=>{
        this.store.employerProfile = res;
        this.dialogRef.close();
    },(err: StiiraError) => this.errorService.setFormModelStateErrors(this.form, err.modelStateErrors));
  }

  public close(canNavigate: boolean): void {
    if (canNavigate) {
      this.dialogRef.close();
    } else {
      if (this.noChanges) {
        this.dialogRef.close();
      } else {
        this.openUnsavedChangesDialog();
      }
    }
  }

  private openUnsavedChangesDialog(): void {
    const dialogConfig: MatDialogConfig = {
      id: UnsavedChangesComponent.componentName,
      width: '300px',
      data: this.store.sysText.unsavedChanges,
    };
    
    this.dialog.open(UnsavedChangesComponent, dialogConfig)
      .afterClosed().subscribe((res: boolean) => {
        if (res) {
          this.dialogRef.close();
        }
      });
  }

  private CreateCustomNameFormGroup(customName: CustomName = null): FormGroup<CustomNameFormGroup> {
    const formGroup =  this.fb.group<CustomNameFormGroup>({
      id: this.fb.control(customName?.id),
      name: this.fb.control(customName?.name),
      customName: this.fb.control(customName?.customName)
    });
    formGroup.controls.name.disable();
    return formGroup;
  }

  private setupForm(): void {
    this.data.employerProfile.leaveTypeCatNames.leaveCategories.forEach(lc => {
      this.form.controls.leaveCategories
        .push(this.CreateCustomNameFormGroup(lc));
    });


    this.form.valueChanges
      .pipe(takeUntil(this.destroy$))
      .subscribe(() => {
        if (!this.formChangeEmitted && !this.noChanges) {
          this.isEditing.emit(true);
          this.formChangeEmitted = true;
        } else if (this.noChanges) {
          this.isEditing.emit(false);
          this.formChangeEmitted = false;
        }
      });
      
    this.formInitValues = { ...this.form.value };
  }

}
